import React, { useEffect, useState, useCallback } from "react";

/**
 * Vポイント OAuth コールバック リダイレクトコンポーネント
 *
 * CCCのVポイント認証後、auth.sokuyaku.jpへリダイレクトされた際に、
 * WEB SOKUYAKU (sokuyaku.jp) またはアプリへパラメータを引き継いでリダイレクトする
 *
 * パラメータ:
 * - nonce_key: CCC認証から返されるワンタイムキー
 * - t_login_tkn: CCC認証から返されるログイントークン
 * - yobi: 予備パラメータ（通常は空）
 * - source: WEB/アプリ判別用パラメータ（web or app）
 * - action: 戻るボタン判別用パラメータ（back）
 */
const VpointRedirect = (props) => {
    const { nonce_key, t_login_tkn, yobi, source, action } = props;
    const [debugInfo, setDebugInfo] = useState("");
    const [redirectStatus, setRedirectStatus] = useState("準備中...");
    const [targetUrl, setTargetUrl] = useState("");
    const [showManualButton, setShowManualButton] = useState(false);
    const [attemptCount, setAttemptCount] = useState(0);

    /**
     * postMessageを送信（リトライ対応）
     */
    const sendPostMessage = useCallback((url, attempt = 1) => {
        console.log(`[VpointRedirect] postMessage送信試行 #${attempt}:`, url);

        if (typeof window === 'undefined') {
            console.log("[VpointRedirect] windowが未定義");
            return false;
        }

        // ReactNativeWebViewのチェック
        const hasWebView = !!window.ReactNativeWebView;
        console.log("[VpointRedirect] ReactNativeWebView存在:", hasWebView);

        if (!hasWebView) {
            console.log("[VpointRedirect] ReactNativeWebViewが見つかりません");
            return false;
        }

        try {
            const message = JSON.stringify({
                type: 'customScheme',
                url: url,
                timestamp: Date.now(),
                attempt: attempt
            });
            console.log("[VpointRedirect] 送信メッセージ:", message);
            window.ReactNativeWebView.postMessage(message);
            console.log("[VpointRedirect] postMessage送信完了");
            return true;
        } catch (e) {
            console.error("[VpointRedirect] postMessageエラー:", e);
            return false;
        }
    }, []);

    /**
     * カスタムスキームURLへリダイレクトを実行
     * 複数の方法を試行して確実にリダイレクトする
     */
    const redirectToUrl = useCallback((url) => {
        console.log("[VpointRedirect] リダイレクト実行:", url);
        setTargetUrl(url);

        const hasWebView = typeof window !== 'undefined' && window.ReactNativeWebView;
        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
        const isSOKUYAKUApp = userAgent.includes('SOKUYAKU-App');

        const debugText = `WebView: ${hasWebView ? 'あり' : 'なし'}\nUA: ${isSOKUYAKUApp ? 'アプリ' : 'ブラウザ'}\nURL: ${url.substring(0, 50)}...`;
        setDebugInfo(debugText);
        console.log("[VpointRedirect] デバッグ情報:", debugText);

        // 方法1: ReactNativeWebViewが存在する場合はpostMessage（複数回試行）
        if (hasWebView) {
            console.log("[VpointRedirect] WebView内で実行、postMessageを使用");
            setRedirectStatus("アプリへ戻っています...");

            // 即座に送信
            sendPostMessage(url, 1);

            // 500ms後にリトライ
            setTimeout(() => {
                sendPostMessage(url, 2);
                setAttemptCount(2);
            }, 500);

            // 1秒後にリトライ
            setTimeout(() => {
                sendPostMessage(url, 3);
                setAttemptCount(3);
            }, 1000);

            // 2秒後に手動ボタンを表示
            setTimeout(() => {
                setShowManualButton(true);
                setRedirectStatus("自動リダイレクトに失敗した場合は、下のボタンをタップしてください");
            }, 2000);

            return;
        }

        // 方法2: WebViewなし - アプリスキームの場合はlocation.hrefを試行
        if (url.startsWith('sokuyaku-')) {
            console.log("[VpointRedirect] WebViewなし、アプリスキーム検出");
            setRedirectStatus("アプリを開いています...");

            // iframeを使った方法を試行
            try {
                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                iframe.src = url;
                document.body.appendChild(iframe);
                console.log("[VpointRedirect] iframe方式を試行");

                // 1秒後にlocation.hrefを試行
                setTimeout(() => {
                    console.log("[VpointRedirect] location.hrefを試行");
                    window.location.href = url;
                }, 1000);

                // 2秒後に手動ボタンを表示
                setTimeout(() => {
                    setShowManualButton(true);
                    setRedirectStatus("自動リダイレクトに失敗した場合は、下のボタンをタップしてください");
                }, 2000);
            } catch (e) {
                console.error("[VpointRedirect] iframe作成エラー:", e);
                window.location.href = url;
            }
            return;
        }

        // 方法3: 通常のHTTPSリダイレクト
        console.log("[VpointRedirect] 通常のリダイレクト");
        setRedirectStatus("リダイレクト中...");
        window.location.href = url;
    }, [sendPostMessage]);

    /**
     * 手動でアプリに戻るボタンのハンドラ
     */
    const handleManualReturn = useCallback(() => {
        console.log("[VpointRedirect] 手動リターンボタン押下");
        setRedirectStatus("アプリへ戻っています...");

        // postMessageを再試行
        if (window.ReactNativeWebView) {
            sendPostMessage(targetUrl, 99);
        }

        // 少し待ってからlocation.hrefを試行
        setTimeout(() => {
            if (targetUrl) {
                window.location.href = targetUrl;
            }
        }, 300);
    }, [targetUrl, sendPostMessage]);

    useEffect(() => {
        // 戻るボタンからの遷移の場合
        if (action === "back") {
            console.log("[VpointRedirect] 戻るボタンからの遷移", { source });

            let targetUrl;

            if (source === "app") {
                // アプリ版: ディープリンクへリダイレクト（キャンセル扱い）
                // Dev: sokuyaku-dev-customer://app.com, Prod: sokuyaku-customer://app.com
                const appBaseUrl = process.env.GATSBY_APP_DEEPLINK_URL || "sokuyaku-dev-customer://app.com";
                targetUrl = `${appBaseUrl}?action=back`;
                console.log("[VpointRedirect] アプリへリダイレクト（戻る）:", targetUrl);
            } else {
                // WEB版（デフォルト）: WEB SOKUYAKUへリダイレクト（キャンセル扱い）
                const webBaseUrl = process.env.GATSBY_WEB_SOKUYAKU_URL || "https://dev.sokuyaku.jp";
                const callbackPath = "/mypage/vpoint/callback";
                targetUrl = `${webBaseUrl}${callbackPath}?action=back`;
                console.log("[VpointRedirect] WEB SOKUYAKUへリダイレクト（戻る）:", targetUrl);
            }

            redirectToUrl(targetUrl);
            return;
        }

        // 通常の認証完了後の遷移
        // 必須パラメータチェック
        if (!nonce_key || !t_login_tkn) {
            console.error("[VpointRedirect] 必須パラメータが不足しています", { nonce_key: !!nonce_key, t_login_tkn: !!t_login_tkn });
            alert("Vポイント認証パラメータが不正です。最初からやり直してください。");
            return;
        }

        // パラメータを構築（URLエンコードは自動で行われる）
        const params = new URLSearchParams({
            nonce_key: nonce_key,
            t_login_tkn: t_login_tkn,
        });

        // 予備パラメータがあれば追加
        if (yobi) {
            params.append("yobi", yobi);
        }

        let targetUrl;

        // sourceパラメータでWEB/アプリを判別
        if (source === "app") {
            // アプリ版: ディープリンクへリダイレクト
            // Dev: sokuyaku-dev-customer://app.com, Prod: sokuyaku-customer://app.com
            // パスに vpoint-callback を含めることでWebViewでの検出を確実にする
            const appBaseUrl = process.env.GATSBY_APP_DEEPLINK_URL || "sokuyaku-dev-customer://app.com";
            targetUrl = `${appBaseUrl}/vpoint-callback?${params.toString()}`;
            console.log("[VpointRedirect] アプリへリダイレクト:", targetUrl);
        } else {
            // WEB版（デフォルト）: WEB SOKUYAKUへリダイレクト
            const webBaseUrl = process.env.GATSBY_WEB_SOKUYAKU_URL || "https://dev.sokuyaku.jp";
            const callbackPath = "/mypage/vpoint/callback";
            targetUrl = `${webBaseUrl}${callbackPath}?${params.toString()}`;
            console.log("[VpointRedirect] WEB SOKUYAKUへリダイレクト:", targetUrl);
        }

        // リダイレクト実行
        redirectToUrl(targetUrl);
    }, [nonce_key, t_login_tkn, yobi, source, action, redirectToUrl]);

    // 表示メッセージ
    const getMessage = () => {
        if (action === "back") {
            return source === "app" ? "アプリ" : "SOKUYAKU";
        }
        return source === "app" ? "アプリ" : "SOKUYAKU";
    };

    return (
        <div style={{ textAlign: "center", marginTop: "2rem", padding: "1rem" }}>
            <h3>Redirecting to SOKUYAKU...</h3>
            <p>{action === "back" ? "キャンセルしました。" : "Vポイント認証完了。"}{getMessage()}へ戻っています...</p>
            <p style={{ color: "#666", fontSize: "0.9em" }}>{redirectStatus}</p>

            {/* 手動リターンボタン */}
            {showManualButton && targetUrl && (
                <div style={{ marginTop: "1.5rem" }}>
                    <button
                        onClick={handleManualReturn}
                        style={{
                            backgroundColor: "#007bff",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            padding: "12px 24px",
                            fontSize: "1rem",
                            fontWeight: "bold",
                            cursor: "pointer",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                        }}
                    >
                        アプリに戻る
                    </button>
                    <p style={{ color: "#999", fontSize: "0.8em", marginTop: "0.5rem" }}>
                        自動リダイレクトが動作しない場合はこちらをタップ
                    </p>
                </div>
            )}

        </div>
    );
};

export default VpointRedirect;
